import { h } from 'preact'
import { signal } from '@preact/signals'
import { useEffect, useState, useCallback } from 'preact/hooks'
import { isEmptyObject } from '../../js/modules/utilities.js'
import { attachClampByQuery } from '../../js/modules/clamp.js'

// Swiper JS
import Swiper from 'swiper'
import { Autoplay, Navigation, Pagination, A11y } from 'swiper/modules'

export const mediaState = signal('')

var articles = {}

export const Media = ({ title, menu, publicite, hideTitle = true }) => {
   // Signal pour articles
   if (mediaState.value !== '' && mediaState.value['mediaArticles'] !== undefined) {
      articles = mediaState.value['mediaArticles']
   }

   let swiper_media

   // Fonction pour détecter si le dispositif est tactile
   const isTouchDevice = useCallback(() => {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
   }, [])

   // Fonction pour détecter si on est en mode mobile
   const isMobileView = useCallback(() => {
      return window.innerWidth < 737
   }, [])

   // Fonction pour détecter si on est en mode tablette
   const isTabletView = useCallback(() => {
      return window.innerWidth >= 737 && window.innerWidth < 961
   }, [])

   // Fonction qui détermine si les flèches de navigation doivent être masquées
   const shouldHideNavigation = useCallback(() => {
      // Toujours cacher en mode mobile
      if (isMobileView()) return true

      // Cacher en mode tablette uniquement sur écran tactile
      if (isTabletView() && isTouchDevice()) return true

      // Afficher dans tous les autres cas
      return false
   }, [isMobileView, isTabletView, isTouchDevice])

   // Fonction pour appliquer attachClampByQuery, à exécuter à chaque changement de résolution
   const applyClamp = useCallback(() => {
      // Clamp : Coupe les titres à 2 lignes + ...
      attachClampByQuery('.media-article [data-clamp-line]')
   }, [])

   useEffect(() => {
      // Appliquer le clamp initial
      applyClamp()

      // Créer un écouteur de redimensionnement pour réappliquer le clamp
      const handleResize = () => {
         applyClamp()

         // Mettre à jour la visibilité des flèches de navigation
         if (swiper_media) {
            const hideNavigation = shouldHideNavigation()
            swiper_media.navigation.enabled = !hideNavigation

            // Sélectionner les boutons de navigation
            const prevButton = document.querySelector('.swiper-media-button-prev')
            const nextButton = document.querySelector('.swiper-media-button-next')

            if (prevButton && nextButton) {
               if (hideNavigation) {
                  prevButton.style.display = 'none'
                  nextButton.style.display = 'none'
               } else {
                  prevButton.style.display = ''
                  nextButton.style.display = ''
               }
            }

            // Ajouter ou supprimer la classe touch-device selon le type d'appareil
            const swiperContainer = document.querySelector('.swiper-media')
            if (swiperContainer) {
               if (isTabletView() && isTouchDevice()) {
                  swiperContainer.classList.add('touch-device')
               } else {
                  swiperContainer.classList.remove('touch-device')
               }
            }
         }
      }

      // Ajouter l'écouteur d'événement pour le resize
      window.addEventListener('resize', handleResize)

      // Swiper ====================================
      swiper_media = new Swiper('.swiper-media', {
         modules: [Autoplay, A11y, Navigation, Pagination],
         navigation: {
            enabled: !shouldHideNavigation(), // Désactiver selon les conditions
            nextEl: '.swiper-media-button-next',
            prevEl: '.swiper-media-button-prev',
         },
         pagination: {
            enabled: true,
            el: '.swiper-media-pagination',
            clickable: true,
            bulletClass: 'swiper-media-bullet',
            bulletActiveClass: 'swiper-media-bullet-active',
            horizontalClass: 'swiper-media-pagination-horizontal',
            renderBullet: function (index, className) {
               return '<span class="' + className + '"><span></span></span>'
            },
         },
         // Rotation
         loop: true,
         autoplay: {
            delay: 8000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
         },
         a11y: {
            enabled: true,
            scrollOnFocus: true,
            containerMessage: false,
            containerRoleDescriptionMessage: false,
            itemRoleDescriptionMessage: false,
            paginationBulletMessage: false,
            prevSlideMessage: false,
            nextSlideMessage: false,
            firstSlideMessage: false,
            lastSlideMessage: false,
            slideLabelMessage: false,
         },
         focusableElements: 'a',
         preventClicks: false,
         // Configuration commune
         spaceBetween: 30,
         slidesPerView: 1,
         slidesPerGroup: 1,

         breakpoints: {
            // when window width is >= 737px
            737: {
               navigation: {
                  enabled: true,
               },
               // Layout pour écrans larges:
               // 6 articles par slide (2 colonnes × 3 lignes)
               // 4 slides au total pour 24 articles
               slidesPerView: 1, // 1 slide à la fois
               slidesPerGroup: 1, // Défile par 1 slide
            },
         },

         on: {
            init: function () {
               // Appliquer la visibilité initiale des flèches
               const hideNavigation = shouldHideNavigation()
               const prevButton = document.querySelector('.swiper-media-button-prev')
               const nextButton = document.querySelector('.swiper-media-button-next')

               if (prevButton && nextButton && hideNavigation) {
                  prevButton.style.display = 'none'
                  nextButton.style.display = 'none'
               }

               // Ajouter la classe touch-device si nécessaire
               const swiperContainer = document.querySelector('.swiper-media')
               if (swiperContainer && isTabletView() && isTouchDevice()) {
                  swiperContainer.classList.add('touch-device')
               }

               // Retirer les tabindex des bullets pour empêcher la navigation clavier dessus
               document.querySelectorAll('.swiper-media-bullet').forEach((bullet) => {
                  bullet.setAttribute('tabindex', '-1')
               })
               document.querySelectorAll('.swiper-media-button').forEach((button) => {
                  button.setAttribute('tabindex', '-1')
               })
            },

            slideChange: function () {
               // Retirer les tabindex des bullets pour empêcher la navigation clavier dessus
               document.querySelectorAll('.swiper-media-bullet').forEach((bullet) => {
                  bullet.setAttribute('tabindex', '-1')
               })
               document.querySelectorAll('.swiper-media-button').forEach((button) => {
                  button.setAttribute('tabindex', '-1')
               })
            },
         },
      })

      // Exécuter un resize initial pour configurer la visibilité des flèches
      handleResize()

      // Nettoyage à la destruction du composant
      return () => {
         window.removeEventListener('resize', handleResize)
      }
   }, [articles, applyClamp, shouldHideNavigation, isTabletView, isTouchDevice])

   const toggleAutoplay = (e) => {
      if (swiper_media.autoplay.running) {
         swiper_media.autoplay.stop()
         e.target.classList.remove('swiper-playpause--pause')
         e.target.classList.add('swiper-playpause--play')
      } else {
         swiper_media.autoplay.start()
         e.target.classList.remove('swiper-playpause--play')
         e.target.classList.add('swiper-playpause--pause')
      }
   }

   // === Pause on focus ===
   const stopAndGoToSlide = (e) => {
      // stop autoplay
      if (swiper_media && swiper_media.autoplay && swiper_media.autoplay.running) {
         swiper_media.autoplay.stop()
         const playPauseBtn = document.querySelector('.swiper-playpause')
         if (playPauseBtn) {
            playPauseBtn.classList.remove('swiper-playpause--pause')
            playPauseBtn.classList.add('swiper-playpause--play')
         }
      }
   }

   return (
      <div class="component-media mb-2">
         <div class="container-fluid">
            <div class="row">
               <div class="col-12">
                  <h2 class={`${hideTitle ? 'sr-only' : ''} no-signature  text-center mw-100 mb-1 mt-2`}>{title}</h2>
                  <Menu menu={menu} />
               </div>
               <div class="col-12 col-md media-container mb-2 mb-md-0">
                  {!isEmptyObject(articles) && <Articles {...articles} toggleAutoplay={toggleAutoplay} nativeAdsIds={publicite.nativeAdsId} stopAndGoToSlide={stopAndGoToSlide} />}
               </div>
               <div class="col-12 col-md media-aside">
                  <div class="media-rm">
                     <div id={publicite.rmAdsId}></div>
                  </div>
               </div>
            </div>
         </div>
      </div>
   )
}

const Menu = ({ menu }) => {
   return (
      <nav class="media-menu" role="navigation" aria-label="News">
         <div class="media-menu-scroll">
            <ul class="font-weight-bold">
               {menu.map((item) => (
                  <li class="pr-2 d-inline-block">
                     <a
                        href={item.URL}
                        data-oevent-category={item.analytics.oeventCategory}
                        data-oevent-action={item.analytics.oeventAction}
                        data-oevent-label={item.analytics.oeventLabel}
                        data-oevent-other={item.analytics.oeventOther}
                        data-pdb-zid={item.analytics.pdbZid}
                        data-pdb-sc={item.analytics.pdbSc}
                        data-pdb-idc={item.analytics.pdbIdc}
                        data-pdb-vi={item.analytics.pdbVi}
                        target={item.target}
                        class="o-link"
                     >
                        {item.image == '' ? item.title : <img src={item.image} alt={item.alt} height="40" loading="lazy" />}
                     </a>
                  </li>
               ))}
            </ul>
         </div>
      </nav>
   )
}

// Composant individuel pour un article
const MediaArticle = ({ item, slideIndex, itemIndex, globalIndex, nativeAdsIds, stopAndGoToSlide }) => {
   // Déterminer si cet article doit avoir un ID spécial (positions 4, 10, 16, 22)
   const specialPositions = [3, 9, 15, 21] // Index 0-based pour les positions 4, 10, 16, 22
   const isSpecialPosition = specialPositions.includes(globalIndex)
   const adsIdIndex = specialPositions.indexOf(globalIndex)
   const divId = isSpecialPosition && nativeAdsIds && nativeAdsIds[adsIdIndex] ? nativeAdsIds[adsIdIndex] : ''

   return (
      <div class="media-article lien-composite" key={`article-${slideIndex}-${itemIndex}`} id={divId} widget-id={divId ? 'SF_6' : null}>
         <div class="d-flex">
            <img class="img" src={item.image} height="65" width="80" alt="" loading="lazy" />
            <div class="content">
               <a
                  href={item.URL}
                  data-oevent-category={item.analytics.oeventCategory}
                  data-oevent-action={item.analytics.oeventAction}
                  data-oevent-label={item.analytics.oeventLabel}
                  data-oevent-other={item.analytics.oeventOther}
                  data-pdb-zid={item.analytics.pdbZid}
                  data-pdb-sc={item.analytics.pdbSc}
                  data-pdb-idc={item.analytics.pdbIdc}
                  data-pdb-vi={item.analytics.pdbVi}
                  onFocus={stopAndGoToSlide}
               >
                  <p class="m-0 p-0">{item.category}</p>
                  <h3 class="title m-0 p-0 font-weight-bold" data-clamp-line="2">
                     {item.title}
                  </h3>
               </a>
            </div>
         </div>
      </div>
   )
}

// Fonction d'utilitaire pour regrouper les articles par paires ou par groupes de 6
const chunkArray = (array, chunkSize) => {
   if (!array) return []
   const result = []
   for (let i = 0; i < array.length; i += chunkSize) {
      result.push(array.slice(i, i + chunkSize))
   }
   return result
}

const Articles = ({ items, title, navigation, toggleAutoplay, nativeAdsIds, stopAndGoToSlide }) => {
   // État pour gérer le mode d'affichage (mobile, tablet, desktop)
   const [displayMode, setDisplayMode] = useState('desktop')

   // Détecter le mode d'affichage au chargement et au resize
   useEffect(() => {
      const checkDisplayMode = () => {
         const width = window.innerWidth
         let newDisplayMode

         if (width < 737) {
            newDisplayMode = 'mobile'
         } else if (width < 961) {
            newDisplayMode = 'tablet'
         } else {
            newDisplayMode = 'desktop'
         }

         // Si l'état du mode d'affichage a changé
         if (newDisplayMode !== displayMode) {
            setDisplayMode(newDisplayMode)

            // Réappliquer le clamp après le changement de layout
            setTimeout(() => {
               attachClampByQuery('.media-article [data-clamp-line]')
            }, 100)
         }
      }

      // Vérification initiale
      checkDisplayMode()

      // Ajouter l'écouteur d'événement pour le resize
      window.addEventListener('resize', checkDisplayMode)

      // Nettoyage à la destruction du composant
      return () => {
         window.removeEventListener('resize', checkDisplayMode)
      }
   }, [displayMode])

   // Appliquer le clamp après le rendu initial et à chaque mise à jour des items
   useEffect(() => {
      attachClampByQuery('.media-article [data-clamp-line]')
   }, [items])

   // Limitation du nombre d'articles selon le mode d'affichage
   const displayItems =
      displayMode === 'mobile'
         ? items.slice(0, 8) // Limiter à 8 articles en mobile
         : displayMode === 'tablet'
         ? items.slice(0, 12) // Limiter à 12 articles en tablet
         : items // Tous les articles en desktop

   // Configuration des articles selon le mode d'affichage
   let slidesArray
   if (displayMode === 'mobile') {
      // Mobile: 2 articles par slide, 4 slides
      slidesArray = chunkArray(displayItems, 2)
   } else if (displayMode === 'tablet') {
      // Tablet (737px-960px): 3 articles par slide, 4 slides
      slidesArray = chunkArray(displayItems, 3)
   } else {
      // Desktop (>960px): 6 articles (3 lignes × 2 colonnes)
      slidesArray = chunkArray(displayItems, 6)
   }

   return (
      <div class="swiper-media">
         <div class="swiper-wrapper">
            {slidesArray.map((slideItems, slideIndex) => (
               <div class="swiper-slide" key={`slide-${slideIndex}`}>
                  {displayMode === 'mobile' ? (
                     // Layout Mobile: 2 articles en colonne
                     <div class="slide-content-mobile">
                        {slideItems.map((item, itemIndex) => (
                           <MediaArticle
                              item={item}
                              slideIndex={slideIndex}
                              itemIndex={itemIndex}
                              globalIndex={slideIndex * 2 + itemIndex}
                              nativeAdsIds={nativeAdsIds}
                              stopAndGoToSlide={stopAndGoToSlide}
                           />
                        ))}
                     </div>
                  ) : displayMode === 'tablet' ? (
                     // Layout Tablet: 3 articles en colonne
                     <div class="slide-content-tablet">
                        {slideItems.map((item, itemIndex) => (
                           <MediaArticle
                              item={item}
                              slideIndex={slideIndex}
                              itemIndex={itemIndex}
                              globalIndex={slideIndex * 3 + itemIndex}
                              nativeAdsIds={nativeAdsIds}
                              stopAndGoToSlide={stopAndGoToSlide}
                           />
                        ))}
                     </div>
                  ) : (
                     // Layout Desktop: 6 articles (3 lignes × 2 colonnes)
                     <div class="slide-content-desktop">
                        {slideItems.map((item, itemIndex) => (
                           <MediaArticle
                              item={item}
                              slideIndex={slideIndex}
                              itemIndex={itemIndex}
                              globalIndex={slideIndex * 6 + itemIndex}
                              nativeAdsIds={nativeAdsIds}
                              stopAndGoToSlide={stopAndGoToSlide}
                           />
                        ))}
                     </div>
                  )}
               </div>
            ))}
         </div>

         <div class="swiper-navigation">
            <div class="swiper-media-pagination" aria-hidden="true" tabIndex="-1" role="presentation"></div>

            <div class="swiper-media-button swiper-media-button-prev" aria-hidden="true" tabIndex="-1">
               <span class="icon-arrow-previous"></span>
            </div>

            <div class="swiper-media-button swiper-media-button-next" aria-hidden="true" tabIndex="-1">
               <span class="icon-arrow-next"></span>
            </div>

            <button type="button" class="swiper-playpause swiper-playpause--pause" onClick={toggleAutoplay} aria-hidden="true" tabIndex="-1"></button>
         </div>
      </div>
   )
}
